// ============================================================================
// Lifespan — M2 walking skeleton (earn, save, accumulate).
//
// The App owns three things and nothing else:
//   1. the Session (engine save/state + samples), held in a ref and advanced
//      ONLY via driver.driveTo — the UI never mutates engine state directly;
//   2. the Clock (wall time -> in-game age), a serializable value;
//   3. persistence wiring (IndexedDB + export/import + lastSeen marker).
// Everything on screen is a pure read model over the session (UX-6).
// ============================================================================

import { useCallback, useEffect, useRef, useState } from 'react';
import { PARAMS } from '@lifespan/engine';
import {
  ageAt,
  clampAge,
  newClock,
  pause,
  run,
  stepBy,
  type Clock,
} from './game/clock.ts';
import {
  driveTo,
  healthModel,
  ledgerIdentity,
  newSession,
  settledAge,
  submitPlan,
  type PlanDraft,
  type Session,
} from './game/driver.ts';
import { bootFrom } from './game/boot.ts';
import {
  backupPersisted,
  exportString,
  importString,
  loadBackupRaw,
  loadPersisted,
  packPersisted,
  storePersisted,
  wipePersisted,
  type LastSeen,
} from './game/persist.ts';
import { ageLabel, money, moneyDelta } from './game/format.ts';
import { JOB_TITLES } from './game/jobs.ts';
import { CashMeter } from './ui/CashMeter.tsx';
import { DeathScreen } from './ui/DeathScreen.tsx';
import { HealthMeter } from './ui/HealthMeter.tsx';
import { Ledger } from './ui/Ledger.tsx';
import { MoraleMeter } from './ui/MoraleMeter.tsx';
import { PlanPanel } from './ui/PlanPanel.tsx';
import { SkillPanel } from './ui/SkillPanel.tsx';
import { TrajectoryView } from './ui/TrajectoryView.tsx';
import { TimeControls } from './ui/TimeControls.tsx';
import { SaveControls } from './ui/SaveControls.tsx';

const P = PARAMS;
const TICK_MS = 250;
const PERSIST_EVERY_MS = 2000;

interface Boot {
  session: Session;
  clock: Clock;
  awayDelta: { deltaS: number; fromAge: number; toAge: number } | null;
}

export function App() {
  const [boot, setBoot] = useState<Boot | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [persistFailed, setPersistFailed] = useState(false);
  const sessionRef = useRef<Session | null>(null);
  const clockRef = useRef<Clock | null>(null);
  const lastPersistRef = useRef(0);
  // bump to re-render after the refs advance (the refs are the truth)
  const [, setRenderTick] = useState(0);
  const forceRender = useCallback(() => setRenderTick((t) => t + 1), []);

  // --- boot: load from IndexedDB, rebuild from the log, settle to now -------
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const now = Date.now();
      let b: Boot | null = null;
      try {
        const stored = await loadPersisted();
        if (stored) b = bootFrom(stored, now, P);
      } catch (e) {
        // A blob that fails validation or replay must never brick the app AND
        // must never be discarded: move it to the backup slot (the fresh
        // session's autosave will overwrite the main key), boot fresh, and
        // surface a recovery banner with a download of the raw blob.
        await backupPersisted().catch(() => {});
        if (!cancelled) setLoadError(e instanceof Error ? e.message : String(e));
      }
      if (!b) {
        b = {
          session: newSession(P, Math.floor(now / 1000)),
          clock: newClock(P.startAge, now),
          awayDelta: null,
        };
      }
      if (!cancelled) {
        sessionRef.current = b.session;
        clockRef.current = b.clock;
        setBoot(b);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // --- persistence ----------------------------------------------------------
  // Every write carries the current {age, S} as the lastSeen marker: "state at
  // the last storage write" (≤2 s stale at any close). A pagehide-only marker
  // would be racy — async IndexedDB writes rarely commit during unload.
  const persist = useCallback(() => {
    const s = sessionRef.current;
    const c = clockRef.current;
    if (!s || !c) return;
    lastPersistRef.current = Date.now();
    const lastSeen: LastSeen = { age: settledAge(s, P), S: s.state.S };
    // Autosave failure must be visible (private browsing, quota, blocked IDB):
    // a silently dead autosave is a data-loss path. Banner until a write lands.
    void storePersisted(packPersisted(s.save, c, lastSeen)).then(
      () => setPersistFailed(false),
      () => setPersistFailed(true),
    );
  }, []);

  // best-effort freshest marker when the tab hides/closes
  useEffect(() => {
    const onHide = () => {
      if (document.visibilityState === 'hidden') persist();
    };
    document.addEventListener('visibilitychange', onHide);
    window.addEventListener('pagehide', persist);
    return () => {
      document.removeEventListener('visibilitychange', onHide);
      window.removeEventListener('pagehide', persist);
    };
  }, [persist]);

  // --- the tick: settle to the clock's reading ------------------------------
  const settleNow = useCallback(() => {
    const s = sessionRef.current;
    const c = clockRef.current;
    if (!s || !c) return;
    const now = Date.now();
    const before = s.state.n;
    const aliveBefore = s.state.alive;
    driveTo(s, clampAge(ageAt(c, now), P.maxAge), P);
    if (!s.state.alive && c.running) {
      clockRef.current = pause(c, now);
    }
    // Fire on change, not on state (death is a transition, then quiescence —
    // a dead life must not re-render 4×/s and rewrite IndexedDB forever).
    if (s.state.n !== before || s.state.alive !== aliveBefore) {
      const died = s.state.alive !== aliveBefore;
      if (died || now - lastPersistRef.current > PERSIST_EVERY_MS) persist();
      forceRender();
    }
  }, [persist, forceRender]);

  useEffect(() => {
    if (!boot) return;
    const id = setInterval(settleNow, TICK_MS);
    return () => clearInterval(id);
  }, [boot, settleNow]);

  // --- actions --------------------------------------------------------------
  // the away banner is a catch-up statement; it goes stale on the first action
  const dismissAway = useCallback(() => {
    setBoot((b) => (b && b.awayDelta ? { ...b, awayDelta: null } : b));
  }, []);

  const onRunBlitz = useCallback(() => {
    clockRef.current = run(clockRef.current!, 'blitz', Date.now());
    dismissAway();
    persist();
    forceRender();
  }, [persist, forceRender, dismissAway]);

  const onPause = useCallback(() => {
    clockRef.current = pause(clockRef.current!, Date.now());
    settleNow();
    persist();
    forceRender();
  }, [persist, forceRender, settleNow]);

  const onStep = useCallback(
    (years: number) => {
      const now = Date.now();
      clockRef.current = stepBy(clockRef.current!, years, now);
      const s = sessionRef.current!;
      driveTo(s, clampAge(ageAt(clockRef.current, now), P.maxAge), P);
      if (!s.state.alive) clockRef.current = pause(clockRef.current, now);
      dismissAway();
      persist();
      forceRender();
    },
    [persist, forceRender, dismissAway],
  );

  const onExport = useCallback(() => {
    const s = sessionRef.current!;
    const c = clockRef.current!;
    // Export a PAUSED clock: a backup file must reopen at the age it was
    // exported at. (The live IDB session keeps its running clock — offline
    // accrual is the M2 contract for the session, not for a detached backup,
    // which would otherwise keep aging and could import as a corpse.)
    const blob = new Blob(
      [
        exportString(
          packPersisted(s.save, pause(c, Date.now()), { age: settledAge(s, P), S: s.state.S }),
        ),
      ],
      { type: 'application/json' },
    );
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `lifespan-save-age${settledAge(s, P).toFixed(2)}.json`;
    a.click();
    URL.revokeObjectURL(url);
  }, []);

  const onImport = useCallback(
    async (file: File) => {
      try {
        const p = importString(await file.text());
        if (
          !confirm(
            'Replace the current life with this imported save? The current life is kept in a one-step backup slot — Export it first if you want a file copy.',
          )
        ) {
          return;
        }
        // Import is as destructive to the current life as Wipe: stash the
        // outgoing blob so one mis-click is always recoverable.
        await backupPersisted().catch(() => {});
        const b = bootFrom(p, Date.now(), P);
        sessionRef.current = b.session;
        clockRef.current = b.clock;
        persist();
        setBoot({ ...b, awayDelta: null });
        forceRender();
      } catch (e) {
        alert(`Import failed: ${e instanceof Error ? e.message : String(e)}`);
      }
    },
    [persist, forceRender],
  );

  const onWipe = useCallback(async () => {
    if (!confirm('Wipe this life and start over? Export first if you want to keep it.')) return;
    await backupPersisted().catch(() => {});
    await wipePersisted().catch(() => {});
    const now = Date.now();
    const session = newSession(P, Math.floor(now / 1000));
    const clock = newClock(P.startAge, now);
    sessionRef.current = session;
    clockRef.current = clock;
    persist();
    setBoot({ session, clock, awayDelta: null });
    forceRender();
  }, [persist, forceRender]);

  const onSubmitPlan = useCallback(
    (draft: PlanDraft) => {
      // settle to the clock's reading first so the entry lands at the true
      // current age, then file through the driver (the only input path)
      settleNow();
      const s = sessionRef.current!;
      // return the driver's actual classification (the tick may have crossed a
      // boundary since the button rendered) so the panel reports the truth
      const kind = submitPlan(s, draft, P);
      dismissAway();
      persist();
      forceRender();
      return kind;
    },
    [persist, forceRender, settleNow, dismissAway],
  );

  const onDownloadBackup = useCallback(async () => {
    const raw = await loadBackupRaw().catch(() => null);
    if (!raw) {
      alert('No backup blob found.');
      return;
    }
    const url = URL.createObjectURL(new Blob([raw], { type: 'application/json' }));
    const a = document.createElement('a');
    a.href = url;
    a.download = 'lifespan-recovered-save.json';
    a.click();
    URL.revokeObjectURL(url);
  }, []);

  // --- render ---------------------------------------------------------------
  if (!boot || !sessionRef.current || !clockRef.current) {
    return <div className="loading">Loading…</div>;
  }
  const session = sessionRef.current;
  const clock = clockRef.current;
  const st = session.state;
  const age = settledAge(session, P);
  const identity = ledgerIdentity(st, P);
  const health = healthModel(session, P);
  // TODO(M7): the Check-up itemised readout is a card unlock — persist it as
  // state written by the Check-up / Doctor's Letter resolution through the
  // logged input path (so replay reproduces the permanent readout and the
  // $4,000 purchase is the only unlock), then remove this dev flag with
  // devMask. Until then: a client-side URL param, honest per ROADMAP M6.
  const showItemised = new URLSearchParams(window.location.search).has('checkup');

  return (
    <div className={`app ${health.finalYears ? 'final-years' : ''}`}>
      <header className="header">
        <div className="brand">
          <h1>LIFESPAN</h1>
          <span className="paramset">ParamSet v{P.paramSetVersion}</span>
        </div>
        <div className="who">
          <span className="age" title="Settled in-game age">
            Age {ageLabel(age)}
          </span>
          <span className="job">
            {JOB_TITLES[st.J] ?? `Tier ${st.J}`} · {money(P.wages[st.J]!)}/yr
          </span>
          {st.alive && health.projected !== null && (
            <span className="projection" title="Projected lifespan under the current plan — exact, and it moves the moment you touch a control">
              Projected {health.projected.toFixed(1)}
            </span>
          )}
          {!st.alive && <span className="dead">† died at {age.toFixed(1)}</span>}
        </div>
      </header>

      {st.alive && (
        <div className="devmask" role="note">
          Dev build — M6 (a whole life). Everything is surfaced: money, morale,
          skills, health, the projection, and death. Event cards land in M7.
        </div>
      )}

      {/* KNOWN DEVIATION (GDD §8 rule 1 / §15.1-6): final-years acceleration
          is not yet "irreversible only forward" — Pause/Step stay available
          after taking Blitz. Harmless in M6 (no real-hour card windows until
          M7, no Live cadence until M9 — nothing timed is farmable), but the
          one-way flag must ship before the first surface that makes slowing
          down worth anything. Flagged in the M6 as-built notes. */}
      {health.finalYears && st.alive && (
        <div className="final-note" role="note">
          Final years — the projection holds at {health.projected!.toFixed(1)}. Death is a
          scheduled scene, not a popup.{' '}
          {!(clock.running && clock.mode === 'blitz') && (
            <button onClick={onRunBlitz}>Play the epilogue at Blitz</button>
          )}
        </div>
      )}

      {loadError && (
        <div className="load-error" role="alert">
          Your stored save failed to load ({loadError}). A fresh life was started; the old
          blob was kept in a backup slot — nothing was deleted.{' '}
          <button onClick={onDownloadBackup}>Download old save</button>{' '}
          <button onClick={() => setLoadError(null)}>Dismiss</button>
        </div>
      )}

      {persistFailed && (
        <div className="persist-warn" role="alert">
          Autosave unavailable — this browser is not storing your life. Use Export to keep it.
        </div>
      )}

      {boot.awayDelta && boot.awayDelta.deltaS !== 0 && (
        <div className="away">
          While you were away: {moneyDelta(boot.awayDelta.deltaS)} (age{' '}
          {boot.awayDelta.fromAge.toFixed(1)} → {boot.awayDelta.toAge.toFixed(1)}){' '}
          <button className="away-dismiss" onClick={dismissAway} aria-label="Dismiss">
            ×
          </button>
        </div>
      )}

      <main className="grid">
        {st.alive ? (
          <>
            <CashMeter session={session} P={P} running={clock.running && st.alive} />
            <MoraleMeter session={session} P={P} clock={clock} />
            <HealthMeter session={session} P={P} clock={clock} showItemised={showItemised} />
            <SkillPanel session={session} P={P} clock={clock} />
            <PlanPanel
              session={session}
              P={P}
              onSubmit={onSubmitPlan}
              currentProjection={health.projected}
            />
            <TrajectoryView session={session} P={P} />
            <Ledger session={session} P={P} />
          </>
        ) : (
          <>
            <DeathScreen session={session} P={P} onRestart={onWipe} onExport={onExport} />
            {/* §9.3: the static grinder/balanced reference curves REMAIN as
                faint ghosts beside the counterfactual — the lived curve, the
                ghosts, and the personal argument share the death frame */}
            <TrajectoryView session={session} P={P} />
            <Ledger session={session} P={P} />
          </>
        )}
      </main>

      <footer className="controls">
        <TimeControls
          clock={clock}
          alive={st.alive}
          onRunBlitz={onRunBlitz}
          onPause={onPause}
          onStep={onStep}
        />
        <SaveControls onExport={onExport} onImport={onImport} onWipe={onWipe} />
      </footer>

      <div className={`ux6 ${identity.diff < 0.01 ? 'ok' : 'bad'}`}>
        UX-6 · ledger reproduces balance to {money(identity.diff, true)}
      </div>
    </div>
  );
}
